import { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import {
  CRUD_COMPONENT_TYPE,
  useDataProvider,
  CRUDComponent,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { FormFields, IconLibrary, PageSection } from '@product-framework/index';
import schema from './schema.json';
import MultiValueSelectorController from './MultiValueSelectorController';
import MultiValueSelectorControllerDependent from './MultiValueSelectorControllerDependent';
import getFormattedApiRequest from '../utils/getFormattedApiRequest';
import getFormattedResponseValues from '../utils/getFormattedResponseValues';
import validationSchema from './validations';

const defaultRow = {
  client: null,
  industry: null,
  brand: null,
  productLine: null,
  model: null,
  serviceSite: null,
  serviceType: null,
  repairSeverity: null,
  repairCode: null,
  laborRate: 0,
  travelRate: 0,
  effectiveDate: null,
  endDate: null,
};

function LaborMatrixForm({
  parentId: laborMatrixId,
  id: rateRuleId,
  action = 'CREATE',
  onClose = () => undefined,
  newPriority,
}) {
  const { formatMessage } = useLocalization();
  const editFormRef = useRef();
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get, post } = useDataProvider(provider);

  const createApiUrl = `${getApiUrl()}/sxcl/v1/labor-matrices/${laborMatrixId}/rate-rules`;
  const updateApiUrl = `${getApiUrl()}/sxcl/v1/labor-matrices/${laborMatrixId}/rate-rules/${rateRuleId}`;
  const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/labor-matrices/${laborMatrixId}/rate-rules/${rateRuleId}`;

  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);

  const preProcessRequestData = data => {
    const { id, ...others } = data;
    return getFormattedApiRequest({
      ...(newPriority !== null && { priority: newPriority }),
      ...others,
    });
  };

  const preProcessFetchData = data => {
    const { ...other } = data;
    return {
      ...other,
    };
  };

  const submitEditForm = async data => {
    setFormErrors(null);
    let response = null;
    const requestData = preProcessRequestData(data);
    if (action === 'CREATE') {
      response = await post(createApiUrl, { ...requestData });
    } else {
      response = await put(updateApiUrl, { ...requestData });
    }
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'LABOR_MATRIX_TABLE_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'Labor Matrix Table was saved successfully',
        }),
      );
      setInitialValues(
        preProcessFetchData(getFormattedResponseValues(response?.data)),
      );
      if (action === 'CREATE') {
        onClose();
      }
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'LABORMATRIXTABLE_UPDATE_FAILURE',
          defaultMessage: 'Error: Could not save the labor matrix table',
        }),
      });
    }
  };

  const fetchInitialData = async () => {
    const response = await get(getPageDataApiUrl);
    if (response?.ok) {
      setInitialValues({
        ...initialValues,
        ...preProcessFetchData(getFormattedResponseValues(response?.data)),
      });

      setLoading(false);
      setFormErrors(null);
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'LABORMATRIXTABLE_DATA_FETCH_FAILURE',
          defaultMessage: 'Error: Could not fetch the labor matrix table data',
        }),
      });
    }
  };

  useEffect(() => {
    if (action === 'EDIT') {
      fetchInitialData(rateRuleId);
    } else {
      setInitialValues(defaultRow);
      setLoading(false);
    }
  }, [rateRuleId]);

  const formProps = {
    customComponents: {
      ...FormFields,
      MultiValueSelectorController,
      MultiValueSelectorControllerDependent,
    },
    schema,
    onSubmit: submitEditForm,
    ref: editFormRef,
    initialValues,
    validationSchema: validationSchema(formatMessage),
  };

  return (
    <Box>
      {loading && (
        <LoadingEvent mt={4}>
          <FormattedMessage id="PROCESSING" defaultMessage="Processing" />
          ...
        </LoadingEvent>
      )}

      {!loading && (
        <PageSection>
          <Box>
            <CRUDComponent
              errors={formErrors}
              type={CRUD_COMPONENT_TYPE.FORM}
              formProps={formProps}>
              <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
                <Button
                  variant="contained"
                  startIcon={<IconLibrary.Save />}
                  color="primary"
                  onClick={() => editFormRef.current?.submit()}>
                  {formatMessage({
                    id: 'SAVE',
                    defaultMessage: 'Save',
                  })}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Reset />}
                  onClick={() => editFormRef.current?.reset()}>
                  {formatMessage({
                    id: 'RESET',
                    defaultMessage: 'Reset',
                  })}
                </Button>
                <Button
                  variant="contained"
                  color="tertiary"
                  startIcon={<IconLibrary.Close />}
                  onClick={onClose}>
                  {formatMessage({
                    id: 'CLOSE',
                    defaultMessage: 'Close',
                  })}
                </Button>
              </Box>
            </CRUDComponent>
          </Box>
        </PageSection>
      )}
    </Box>
  );
}

export default observer(LaborMatrixForm);
